/**
 * @fileoverview Early access application form section for landing page.
 * Includes beta notice alert and waitlist signup form.
 * @module features/landing/components/EarlyAccessSection
 */

import { ReactElement, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'
import { ArrowLeft, ArrowRight, Rocket, Send } from 'lucide-react'
import { BorderBeam } from '@/components/ui/border-beam'
import { submitEarlyAccessLead } from '../api/earlyAccessApi'

/**
 * Form field wrapper with label and error display.
 */
function FormField({
  label,
  htmlFor,
  optionalLabel,
  error,
  children,
}: {
  label: string
  htmlFor: string
  optionalLabel?: string | undefined
  error?: string | undefined
  children: React.ReactNode
}): ReactElement {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm text-foreground/80">
        {label}
        {optionalLabel && <span className="text-muted-foreground/60 ml-1 font-normal">{optionalLabel}</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

interface FormErrors {
  email?: string
  company?: string
}

/**
 * Early access application form section.
 * Displays beta notice and collects user information for waitlist.
 *
 * @component
 */
export function EarlyAccessSection(): ReactElement {
  const { t } = useTranslation('features-landing')
  const formRef = useRef<HTMLFormElement>(null)
  const [step, setStep] = useState(1)
  const [revenueIndex, setRevenueIndex] = useState(2)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Store step 1 values since inputs are conditionally rendered
  const [step1Data, setStep1Data] = useState({ email: '', company: '' })

  const validateStep1 = (form: HTMLFormElement): boolean => {
    const formData = new FormData(form)
    const email = (formData.get('email') as string)?.trim() ?? ''
    const company = (formData.get('company') as string)?.trim() ?? ''
    const newErrors: FormErrors = {}

    if (!email) {
      newErrors.email = t('earlyAccess.form.validation.emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('earlyAccess.form.validation.emailInvalid')
    }

    if (!company) {
      newErrors.company = t('earlyAccess.form.validation.companyRequired')
    }

    setErrors(newErrors)

    if (Object.keys(newErrors).length === 0) {
      setStep1Data({ email, company })
      return true
    }
    return false
  }

  const handleContinue = (): void => {
    setStep(2)
  }

  const handleBack = (): void => {
    setStep(1)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    setSubmitError(null)

    if (step === 1) {
      if (!validateStep1(e.currentTarget)) {
        return
      }
      handleContinue()
      return
    }

    if (!formRef.current) return

    setIsSubmitting(true)

    const formData = new FormData(formRef.current)
    const result = await submitEarlyAccessLead({
      email: step1Data.email,
      company: step1Data.company,
      website: (formData.get('website') as string) || undefined,
      monthly_revenue_index: revenueIndex,
    })

    setIsSubmitting(false)

    if (result.success) {
      setIsSubmitted(true)
    } else if (result.error === 'duplicate_email') {
      setSubmitError(t('earlyAccess.form.validation.emailExists'))
      setStep(1)
    } else {
      setSubmitError(t('earlyAccess.form.validation.submitFailed'))
    }
  }

  return (
    <section id="early-access" className="py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto space-y-8">
          {/* Beta notice alert */}
          <Alert className="bg-card/50 border-primary/30">
            <Rocket className="h-4 w-4 text-primary" />
            <AlertTitle className="text-foreground">
              {t('earlyAccess.betaNotice.title')}
            </AlertTitle>
            <AlertDescription className="text-muted-foreground">
              {t('earlyAccess.betaNotice.description')}
            </AlertDescription>
          </Alert>

          {/* Application Form Card */}
          <div className="glass-card rounded-3xl p-8">
            <div className="flex flex-col items-center mb-8">
              <img
                src="/abyssblock-icon.webp"
                alt="AbyssBlock logo"
                className="w-16 h-16 mb-4 rounded-xl"
              />
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground text-center">
                {t('earlyAccess.title')}
              </h2>
            </div>
            {isSubmitted ? (
              <div className="text-center py-12 space-y-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-positive/20 flex items-center justify-center">
                  <Rocket className="h-8 w-8 text-positive" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">
                  {t('earlyAccess.success.title')}
                </h3>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-6">
                {submitError && (
                  <Alert variant="destructive" className="bg-destructive/10">
                    <AlertDescription>{submitError}</AlertDescription>
                  </Alert>
                )}
                {step === 1 ? (
                  <>
                    {/* Step 1: Email + Company */}
                    <div className="flex flex-col gap-4">
                      <FormField label={t('earlyAccess.form.email')} htmlFor="email" error={errors.email}>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder={t('earlyAccess.form.emailPlaceholder')}
                          className="bg-[oklch(0.18_0.02_290)]"
                        />
                      </FormField>

                      <FormField label={t('earlyAccess.form.company')} htmlFor="company" error={errors.company}>
                        <Input
                          id="company"
                          name="company"
                          placeholder={t('earlyAccess.form.companyPlaceholder')}
                          className="bg-[oklch(0.18_0.02_290)]"
                        />
                      </FormField>
                    </div>

                    <div className="relative overflow-hidden rounded-md">
                      <Button
                        type="submit"
                        variant="outline"
                        className="w-full h-11 text-base"
                      >
                        <span className="flex items-center gap-2">
                          {t('earlyAccess.form.continue')}
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </Button>
                      <BorderBeam size={80} duration={10} />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Step 2: Website + Revenue */}
                    <FormField label={t('earlyAccess.form.website')} htmlFor="website">
                      <Input
                        id="website"
                        name="website"
                        type="text"
                        placeholder={t('earlyAccess.form.websitePlaceholder')}
                        className="bg-[oklch(0.18_0.02_290)]"
                      />
                    </FormField>

                    <div className="space-y-4">
                      <Label className="text-sm text-foreground/80">
                        {t('earlyAccess.form.monthlyRevenue')}
                        <span className="text-muted-foreground/60 ml-1 font-normal">{t('earlyAccess.form.referenceOnly')}</span>
                      </Label>
                      <div className="px-1">
                        <Slider
                          value={[revenueIndex]}
                          onValueChange={(values) => setRevenueIndex(values[0] ?? 0)}
                          min={0}
                          max={9}
                          step={1}
                          className="w-full"
                        />
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground/50">{t('earlyAccess.revenueRanges.0')}</span>
                        <span className="font-medium text-foreground">
                          {t(`earlyAccess.revenueRanges.${revenueIndex}`)}
                        </span>
                        <span className="text-muted-foreground/50">{t('earlyAccess.revenueRanges.9')}</span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleBack}
                        className="h-11"
                      >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        {t('earlyAccess.form.back')}
                      </Button>
                      <Button
                        type="submit"
                        variant="outline"
                        disabled={isSubmitting}
                        className="flex-1 h-11 text-base"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            {t('earlyAccess.form.submitting')}
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send className="h-4 w-4" />
                            {t('earlyAccess.form.submit')}
                          </span>
                        )}
                      </Button>
                    </div>
                  </>
                )}

                <p className="text-xs text-muted-foreground/60 text-center">
                  {t('earlyAccess.form.privacyNote')}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
